import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, romeDateString } from "@/lib/api";
import { BellRing, ChevronLeft, Calendar, Filter, ChefHat, User, MessageSquare, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const ChiamateArchivio = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(romeDateString());
    const [tables, setTables] = useState([]);
    const [tableFilter, setTableFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState(""); // "" | "customer" | "kitchen"
    const [statusFilter, setStatusFilter] = useState(""); // "" | "pending" | "dismissed"
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadTables = async () => {
        try {
            const { data } = await api.get("/tables");
            setTables(data);
        } catch { /* */ }
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ date });
            if (tableFilter) params.set("table_id", tableFilter);
            if (sourceFilter) params.set("source", sourceFilter);
            if (statusFilter) params.set("status", statusFilter);
            const { data } = await api.get(`/waiter-calls/archive?${params.toString()}`);
            setCalls(data);
        } catch {
            toast.error("Errore nel caricamento dell'archivio chiamate");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTables(); }, []);
    useEffect(() => { load(); }, [date, tableFilter, sourceFilter, statusFilter]);

    const dismiss = async (callId) => {
        try {
            await api.post(`/waiter-calls/${callId}/dismiss`);
            toast.success("Chiamata segnata come gestita");
            load();
        } catch {
            toast.error("Errore");
        }
    };

    const setQuickDate = (offset) => {
        const d = new Date();
        d.setDate(d.getDate() + offset);
        setDate(romeDateString(d));
    };

    const groups = useMemo(() => {
        const byHour = {};
        for (const c of calls) {
            const hour = (c.hour_bucket || "??").slice(0, 2) + ":00";
            if (!byHour[hour]) byHour[hour] = [];
            byHour[hour].push(c);
        }
        return Object.entries(byHour).sort((a, b) => b[0].localeCompare(a[0]));
    }, [calls]);

    const stats = useMemo(() => {
        const fromCustomer = calls.filter((c) => c.source === "customer").length;
        const fromKitchen = calls.filter((c) => c.source === "kitchen").length;
        const pending = calls.filter((c) => c.status === "pending").length;
        return { fromCustomer, fromKitchen, pending };
    }, [calls]);

    return (
        <div className="space-y-6" data-testid="chiamate-archivio-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <button onClick={() => navigate(-1)} className="text-elegant hover:text-brand text-sm flex items-center gap-1 mb-2" data-testid="back-button">
                        <ChevronLeft className="w-4 h-4" /> Indietro
                    </button>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-1">Archivio</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-3">
                        <BellRing className="w-9 h-9 text-brand" /> Archivio Chiamate Camerieri
                    </h1>
                    <p className="text-elegant text-sm mt-2">
                        Tutte le chiamate ricevute dai camerieri — dai clienti tramite app e dalla cucina.
                    </p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <StatCard icon={User} label="Da clienti" value={stats.fromCustomer} testid="stat-customer" />
                    <StatCard icon={ChefHat} label="Da cucina" value={stats.fromKitchen} testid="stat-kitchen" color="purple" />
                    <StatCard icon={Clock} label="In attesa" value={stats.pending} testid="stat-pending" color="amber" />
                </div>
            </div>

            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="archive-filters">
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-elegant flex items-center gap-1"><Calendar className="w-3 h-3" /> Data</label>
                    <div className="flex gap-1 items-center">
                        <button onClick={() => setQuickDate(-1)} className="h-10 px-2 border border-[#1f2c44] rounded text-elegant hover:text-brand text-xs" data-testid="prev-day">←</button>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                            data-testid="date-input"
                            className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm focus:border-brand focus:outline-none text-sm" />
                        <button onClick={() => setQuickDate(1)} className="h-10 px-2 border border-[#1f2c44] rounded text-elegant hover:text-brand text-xs" data-testid="next-day">→</button>
                        <button onClick={() => setDate(romeDateString())} className="h-10 px-3 border border-brand text-brand rounded text-xs uppercase tracking-wider hover:bg-brand/10" data-testid="today-btn">Oggi</button>
                    </div>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-elegant flex items-center gap-1"><Filter className="w-3 h-3" /> Origine</label>
                    <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} data-testid="source-filter"
                        className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm text-sm focus:border-brand focus:outline-none">
                        <option value="">Tutte</option>
                        <option value="customer">Cliente</option>
                        <option value="kitchen">Cucina</option>
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-elegant">Tavolo</label>
                    <select value={tableFilter} onChange={(e) => setTableFilter(e.target.value)} data-testid="table-filter"
                        className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm text-sm focus:border-brand focus:outline-none">
                        <option value="">Tutti</option>
                        {tables.map((t) => <option key={t.id} value={t.id}>Tavolo {t.number}</option>)}
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] uppercase tracking-wider text-elegant">Stato</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter"
                        className="h-10 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm text-sm focus:border-brand focus:outline-none">
                        <option value="">Tutti</option>
                        <option value="pending">In attesa</option>
                        <option value="dismissed">Gestite</option>
                    </select>
                </div>
            </div>

            {loading && <div className="text-elegant text-sm">Caricamento...</div>}
            {!loading && calls.length === 0 && (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-12 text-center text-elegant" data-testid="no-calls">
                    <BellRing className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    Nessuna chiamata in questa data.
                </div>
            )}

            <div className="space-y-6">
                {groups.map(([hour, items]) => (
                    <div key={hour} data-testid={`hour-${hour}`}>
                        <div className="text-xs uppercase tracking-[0.3em] text-brand mb-3 flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5" /> {hour} — {items.length} {items.length === 1 ? "chiamata" : "chiamate"}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {items.map((c) => (
                                <div key={c.id} className={`bg-surface border rounded-lg p-4 ${
                                    c.source === "kitchen" ? "border-purple-700/50" : "border-[#1f2c44]"
                                } ${c.status === "pending" ? "ring-1 ring-amber-700/30" : "opacity-80"}`} data-testid={`call-${c.id}`}>
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2">
                                            {c.source === "kitchen" ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-purple-900/40 text-purple-200 px-2 py-1 rounded">
                                                    <ChefHat className="w-3 h-3" /> Cucina
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-brand-soft text-brand px-2 py-1 rounded">
                                                    <User className="w-3 h-3" /> Cliente
                                                </span>
                                            )}
                                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                                                c.status === "pending" ? "bg-amber-900/40 text-amber-300 animate-pulse" : "bg-green-900/30 text-green-400"
                                            }`}>
                                                {c.status === "pending" ? "In attesa" : "Gestita"}
                                            </span>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] uppercase tracking-wider text-elegant">Tavolo</div>
                                            <div className="font-display text-2xl font-bold text-warm">{c.table_number}</div>
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-elegant mb-2">
                                        {c.hour_bucket}
                                        {c.source === "customer" && c.customer_name && <> · {c.customer_name}</>}
                                        {c.source === "kitchen" && c.kitchen_user && <> · {c.kitchen_user}</>}
                                    </div>
                                    {c.note ? (
                                        <div className="bg-deep border border-[#1f2c44] rounded p-2 text-sm text-warm italic flex items-start gap-2">
                                            <MessageSquare className="w-3.5 h-3.5 text-brand flex-shrink-0 mt-0.5" />
                                            <span>{c.note}</span>
                                        </div>
                                    ) : (
                                        <div className="text-[11px] text-elegant italic">Nessuna nota</div>
                                    )}
                                    {c.status === "pending" && (
                                        <button onClick={() => dismiss(c.id)} data-testid={`dismiss-${c.id}`}
                                            className="w-full mt-3 h-9 border border-brand text-brand hover:bg-brand/10 rounded text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Segna come gestita
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const StatCard = ({ icon: Icon, label, value, color = "brand", testid }) => {
    const colorMap = {
        brand: "border-brand bg-brand-soft text-brand",
        purple: "border-purple-700/50 bg-purple-900/20 text-purple-200",
        amber: "border-amber-700/50 bg-amber-900/20 text-amber-300",
    };
    return (
        <div className={`border rounded-lg px-4 py-3 ${colorMap[color]}`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider opacity-80 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
            <div className="font-display text-3xl font-bold">{value}</div>
        </div>
    );
};

export default ChiamateArchivio;
